// apps/admin-ui/src/components/widgets/AuditEvents.tsx
// Audit Events widget — shows recent audit log entries from the KYC service
// with expandable rows for structured details (user_info_edited diffs).
// ALL RIGHTS RESERVED
//
// ADDED 2026-02-07: Initial implementation per HANDOFF-admin-ui-audit-events-widget.md

import { useState, useEffect, useCallback } from 'react';
import { getCurrentEmployee } from '../../utils/auth';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AuditLogDetails {
  before?: Record<string, unknown>;
  after?: Record<string, unknown>;
  fieldsChanged?: string[];
  otpVerified?: boolean;
  [key: string]: unknown;
}

interface AuditLogEntry {
  id: string;
  timestamp: string;
  action: string;
  employeeId: string;
  employeeName: string;
  employeeRole: string;
  registrationId: string | null;
  ipAddress: string;
  reason: string;
  details: AuditLogDetails | null;
}

interface AuditResponse {
  logs: AuditLogEntry[];
  total: number;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const KYC_URL = import.meta.env.VITE_KYC_URL || 'http://localhost:3002';

const HIGH_PRIORITY_ACTIONS = ['user_info_edited', 'registration_approved', 'registration_rejected', 'override_submitted'];

const ACTION_FILTER_OPTIONS = [
  { value: '', label: 'All Actions' },
  { value: 'user_info_edited', label: 'User Info Edited' },
  { value: 'registration_approved', label: 'Approved' },
  { value: 'registration_rejected', label: 'Rejected' },
  { value: 'override_submitted', label: 'Override Submitted' },
  { value: 'persona_inquiry_viewed', label: 'Persona Inquiry' },
  { value: 'status_changed', label: 'Status Changed' },
  { value: 'document_verified', label: 'Doc Verified' },
  { value: 'login', label: 'Login' },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatFieldName(field: string): string {
  return field
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ')
    .replace(/^\w/, c => c.toUpperCase())
    .trim();
}

function formatTimestamp(ts: string): string {
  const d = new Date(ts);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMin / 60);

  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;

  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatValue(val: unknown): string {
  if (val === null || val === undefined) return '(empty)';
  if (typeof val === 'boolean') return val ? 'true' : 'false';
  return String(val);
}

function actionColor(action: string): string {
  if (action === 'user_info_edited') return '#ef4444';
  if (action.includes('approved')) return '#22c55e';
  if (action.includes('rejected')) return '#f59e0b';
  if (action.includes('override') || action.includes('escalat')) return '#ec4899';
  if (action.includes('login') || action.includes('logout')) return '#6b7280';
  return '#3b82f6';
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function ActionBadge({ action }: { action: string }) {
  const color = actionColor(action);
  const label = action.replace(/_/g, ' ');

  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: '10px',
      fontSize: '0.6875rem',
      fontWeight: 600,
      background: `${color}18`,
      color,
      textTransform: 'capitalize',
      whiteSpace: 'nowrap',
    }}>
      {label}
    </span>
  );
}

function OtpBadge() {
  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '3px',
      padding: '1px 6px',
      borderRadius: '8px',
      fontSize: '0.625rem',
      fontWeight: 600,
      background: 'rgba(34, 197, 94, 0.12)',
      color: '#22c55e',
      whiteSpace: 'nowrap',
    }}>
      ✓ OTP verified
    </span>
  );
}

function BeforeAfterDiff({ details }: { details: AuditLogDetails }) {
  const { before, after, fieldsChanged } = details;
  if (!before || !after || !fieldsChanged?.length) return null;

  return (
    <table style={{ width: '100%', fontSize: '0.8125rem', borderCollapse: 'collapse', marginTop: '0.5rem' }}>
      <thead>
        <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
          <th style={{ ...diffThStyle }}>Field</th>
          <th style={{ ...diffThStyle }}>Before</th>
          <th style={{ ...diffThStyle }}>After</th>
        </tr>
      </thead>
      <tbody>
        {fieldsChanged.map(field => (
          <tr key={field} style={{ borderBottom: '1px solid var(--color-border-subtle)' }}>
            <td style={{ ...diffTdStyle, fontWeight: 500 }}>
              {formatFieldName(field)}
            </td>
            <td style={{
              ...diffTdStyle,
              background: 'rgba(239, 68, 68, 0.06)',
              color: 'var(--color-text-secondary)',
              fontFamily: 'var(--font-mono)',
            }}>
              {formatValue(before[field])}
            </td>
            <td style={{
              ...diffTdStyle,
              background: 'rgba(34, 197, 94, 0.06)',
              color: 'var(--color-text)',
              fontFamily: 'var(--font-mono)',
              fontWeight: 500,
            }}>
              {formatValue(after[field])}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function GenericDetails({ details }: { details: AuditLogDetails }) {
  // Filter out the keys that have their own renderers
  const keys = Object.keys(details).filter(
    k => !['before', 'after', 'fieldsChanged', 'otpVerified'].includes(k)
  );
  if (keys.length === 0) return null;

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'auto 1fr',
      gap: '0.25rem 1rem',
      fontSize: '0.8125rem',
      marginTop: '0.5rem',
      padding: '0.5rem 0.75rem',
      background: 'var(--color-bg-subtle)',
      borderRadius: '6px',
    }}>
      {keys.map(key => (
        <React.Fragment key={key}>
          <span style={{ color: 'var(--color-text-muted)', fontWeight: 500 }}>
            {formatFieldName(key)}
          </span>
          <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--color-text-secondary)' }}>
            {typeof details[key] === 'object' ? JSON.stringify(details[key]) : formatValue(details[key])}
          </span>
        </React.Fragment>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Expanded row detail panel
// ---------------------------------------------------------------------------

function AuditDetailPanel({ entry }: { entry: AuditLogEntry }) {
  const { details, action } = entry;

  if (!details) {
    return (
      <div style={{ padding: '0.75rem', color: 'var(--color-text-muted)', fontSize: '0.8125rem' }}>
        No structured details available.
      </div>
    );
  }

  const isEditAction = action === 'user_info_edited';

  return (
    <div style={{ padding: '0.5rem 0.75rem 0.75rem' }}>
      {/* OTP badge */}
      {details.otpVerified && (
        <div style={{ marginBottom: '0.5rem' }}>
          <OtpBadge />
        </div>
      )}

      {/* Before/after diff for user_info_edited */}
      {isEditAction && <BeforeAfterDiff details={details} />}

      {/* Generic key-value for other action types (or extra fields) */}
      <GenericDetails details={details} />

      {/* Meta info */}
      <div style={{
        marginTop: '0.75rem',
        paddingTop: '0.5rem',
        borderTop: '1px solid var(--color-border-subtle)',
        display: 'flex',
        gap: '1.5rem',
        fontSize: '0.75rem',
        color: 'var(--color-text-muted)',
      }}>
        <span>IP: {entry.ipAddress}</span>
        <span>Event #{entry.id}</span>
        {entry.registrationId && <span>Reg: #{entry.registrationId}</span>}
        <span>{new Date(entry.timestamp).toLocaleString()}</span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

import React from 'react';

export function AuditEvents() {
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [actionFilter, setActionFilter] = useState('');

  const fetchAuditLogs = useCallback(async () => {
    try {
      setLoading(true);

      // Build auth headers from logged-in employee
      const employee = getCurrentEmployee();
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (employee) {
        headers['x-dev-employee-id'] = employee.id;
        headers['x-dev-employee-role'] = employee.roles[0] || 'security_admin';
        headers['x-dev-employee-roles'] = JSON.stringify(employee.roles);
      }

      const params = new URLSearchParams({ limit: '20' });
      if (actionFilter) params.set('action', actionFilter);

      const response = await fetch(`${KYC_URL}/csr/audit?${params}`, { headers });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data: AuditResponse = await response.json();
      setLogs(data.logs);
      setTotal(data.total);
      setError(null);
    } catch (err: any) {
      console.error('Failed to fetch audit events:', err);
      setError(err.message || 'Failed to load');
      // Provide fallback data for development
      setLogs([
        {
          id: '42',
          timestamp: new Date(Date.now() - 300000).toISOString(),
          action: 'user_info_edited',
          employeeId: '20',
          employeeName: 'CSR Agent',
          employeeRole: 'csr',
          registrationId: '6',
          ipAddress: '127.0.0.1',
          reason: 'Changed: middleName, lastName',
          details: {
            before: { middleName: null, lastName: 'Smith' },
            after: { middleName: 'J', lastName: 'Jones' },
            fieldsChanged: ['middleName', 'lastName'],
            otpVerified: true,
          },
        },
        {
          id: '41',
          timestamp: new Date(Date.now() - 900000).toISOString(),
          action: 'registration_approved',
          employeeId: '20',
          employeeName: 'CSR Agent',
          employeeRole: 'csr',
          registrationId: '5',
          ipAddress: '127.0.0.1',
          reason: 'Approved after document verification',
          details: null,
        },
        {
          id: '40',
          timestamp: new Date(Date.now() - 1800000).toISOString(),
          action: 'persona_inquiry_viewed',
          employeeId: '20',
          employeeName: 'CSR Agent',
          employeeRole: 'csr',
          registrationId: '6',
          ipAddress: '127.0.0.1',
          reason: 'Viewed Persona inquiry for registration #6',
          details: { inquiryId: 'inq_abc123', provider: 'persona' },
        },
      ]);
      setTotal(3);
    } finally {
      setLoading(false);
    }
  }, [actionFilter]);

  // Fetch on mount and when filter changes
  useEffect(() => {
    fetchAuditLogs();
  }, [fetchAuditLogs]);

  // Auto-refresh every 30s
  useEffect(() => {
    const interval = setInterval(fetchAuditLogs, 30000);
    return () => clearInterval(interval);
  }, [fetchAuditLogs]);

  const toggleExpand = (id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // ─── Loading ─────────────────────────────────────────────────
  if (loading && logs.length === 0) {
    return (
      <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--color-text-secondary)' }}>
        Loading audit events...
      </div>
    );
  }

  // ─── Error with no data ──────────────────────────────────────
  if (!loading && logs.length === 0 && error) {
    return (
      <div style={{ padding: '1rem', textAlign: 'center' }}>
        <p style={{ color: 'var(--color-error)' }}>Failed to load audit events</p>
        <button onClick={fetchAuditLogs} style={{ marginTop: '0.5rem', padding: '0.25rem 0.5rem' }}>
          Retry
        </button>
      </div>
    );
  }

  // ─── Main render ─────────────────────────────────────────────
  return (
    <div>
      {/* Filter bar */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '0.75rem',
      }}>
        <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
          {total} total events
        </div>
        <select
          value={actionFilter}
          onChange={e => setActionFilter(e.target.value)}
          style={{
            padding: '0.25rem 0.5rem',
            fontSize: '0.75rem',
            borderRadius: '6px',
            border: '1px solid var(--color-border)',
            background: 'var(--color-bg)',
            color: 'var(--color-text)',
          }}
        >
          {ACTION_FILTER_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      {/* Event table */}
      <table style={{ width: '100%', fontSize: '0.8125rem', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
            <th style={thStyle}>Time</th>
            <th style={thStyle}>Action</th>
            <th style={thStyle}>Employee</th>
            <th style={thStyle}>Reg</th>
            <th style={thStyle}>Summary</th>
          </tr>
        </thead>
        <tbody>
          {logs.map(entry => {
            const isExpanded = expandedIds.has(entry.id);
            const hasDetails = entry.details !== null;
            const isHighPriority = HIGH_PRIORITY_ACTIONS.includes(entry.action);

            return (
              <React.Fragment key={entry.id}>
                <tr
                  onClick={() => hasDetails && toggleExpand(entry.id)}
                  style={{
                    borderBottom: isExpanded ? 'none' : '1px solid var(--color-border-subtle)',
                    cursor: hasDetails ? 'pointer' : 'default',
                    background: isHighPriority ? 'rgba(239, 68, 68, 0.03)' : undefined,
                  }}
                >
                  <td style={{ ...tdStyle, whiteSpace: 'nowrap', color: 'var(--color-text-muted)', fontVariantNumeric: 'tabular-nums' }}>
                    {formatTimestamp(entry.timestamp)}
                  </td>
                  <td style={tdStyle}>
                    <ActionBadge action={entry.action} />
                  </td>
                  <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                    <span style={{ fontWeight: 500 }}>{entry.employeeName}</span>
                    <span style={{
                      marginLeft: '0.35rem',
                      fontSize: '0.6875rem',
                      color: 'var(--color-text-muted)',
                    }}>
                      {entry.employeeRole}
                    </span>
                  </td>
                  <td style={{ ...tdStyle, fontFamily: 'var(--font-mono)', color: 'var(--color-text-secondary)' }}>
                    {entry.registrationId ? `#${entry.registrationId}` : '—'}
                  </td>
                  <td style={{ ...tdStyle, color: 'var(--color-text-secondary)', maxWidth: '260px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <span>{entry.reason}</span>
                    {hasDetails && (
                      <span style={{
                        marginLeft: '0.5rem',
                        fontSize: '0.625rem',
                        color: 'var(--color-text-muted)',
                      }}>
                        {isExpanded ? '▾' : '▸'}
                      </span>
                    )}
                    {entry.details?.otpVerified && (
                      <span style={{ marginLeft: '0.5rem' }}><OtpBadge /></span>
                    )}
                  </td>
                </tr>

                {/* Expanded detail row */}
                {isExpanded && hasDetails && (
                  <tr>
                    <td
                      colSpan={5}
                      style={{
                        padding: 0,
                        borderBottom: '1px solid var(--color-border-subtle)',
                        background: 'var(--color-bg-subtle)',
                      }}
                    >
                      <AuditDetailPanel entry={entry} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>

      {/* Footer */}
      {error && (
        <div style={{
          marginTop: '0.75rem',
          paddingTop: '0.5rem',
          borderTop: '1px solid var(--color-border)',
          fontSize: '0.6875rem',
          color: 'var(--color-text-muted)',
          textAlign: 'center',
        }}>
          Using fallback data — live endpoint unavailable
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Table styles
// ---------------------------------------------------------------------------

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '0.5rem 0.75rem',
  fontWeight: 500,
  color: 'var(--color-text-secondary)',
  fontSize: '0.75rem',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
};

const tdStyle: React.CSSProperties = {
  padding: '0.5rem 0.75rem',
};

const diffThStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '0.375rem 0.625rem',
  fontWeight: 500,
  color: 'var(--color-text-muted)',
  fontSize: '0.75rem',
};

const diffTdStyle: React.CSSProperties = {
  padding: '0.375rem 0.625rem',
};

export default AuditEvents;

